import { Button } from "@fp/ui/components/base/buttons/button";
import { Input } from "@fp/ui/components/base/input/input";
import { RadioButton, RadioGroup } from "@fp/ui/components/base/radio-buttons/radio-buttons";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { getPayeeOrganizationCandidates, useCreatePayeeOrganization } from "@fp/admin-api-client/payee-organizations/payee-organizations.client";
import { useCreateInvitation } from "@fp/admin-api-client/sent-invitations/sent-invitations.client";
import { InvitationType } from "@fp/admin-api-client/types/invitationType";
import type { PayeeOrganizationCandidateDto } from "@fp/admin-api-client/types/payeeOrganizationCandidateDto";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useState } from "react";
import { getErrorMessage, type ApiErrorResponse } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TALENT_ROLE = "ORG_TALENT";
const PERSON_CHOICE = "person";

export const Route = createFileRoute("/roster/add")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const router = useRouter();

	const [email, setEmail] = useState("");
	const [lookedUpEmail, setLookedUpEmail] = useState<string | null>(null);
	const [candidates, setCandidates] = useState<PayeeOrganizationCandidateDto[]>([]);
	const [fundingSourceMissing, setFundingSourceMissing] = useState(false);
	const [choice, setChoice] = useState(PERSON_CHOICE);
	const [isLookingUp, setIsLookingUp] = useState(false);

	const trimmedEmail = email.trim();
	const isEmailValid = EMAIL_PATTERN.test(trimmedEmail);
	const hasLookedUp = lookedUpEmail === trimmedEmail;

	const createPayeeOrganizationMutation = useCreatePayeeOrganization({
		mutation: {
			onSuccess: async (response) => {
				await router.invalidate();
				toast.success(`${response.data.name} is on your roster`);
				navigate({ to: "/roster" });
			},
			onError: (err) => {
				toast.error(getErrorMessage(err, "Failed to add the organization to your roster"));
			},
		},
	});

	const createInvitationMutation = useCreateInvitation({
		mutation: {
			onSuccess: () => {
				toast.success("Invitation sent!");
				navigate({ to: "/invitations" });
			},
			onError: (err) => {
				if (
					isAxiosError<ApiErrorResponse>(err) &&
					err.response?.status === 409 &&
					err.response.data?.code === "ALREADY_MEMBER" &&
					err.response.data.message
				) {
					toast.warning(err.response.data.message);
				} else {
					toast.error(getErrorMessage(err, "Failed to send invitation. Please try again."));
				}
			},
		},
	});

	const handleLookup = async () => {
		setIsLookingUp(true);
		try {
			const response = await getPayeeOrganizationCandidates({ email: trimmedEmail });
			setCandidates(response.data);
			setFundingSourceMissing(false);
			setChoice(response.data[0]?.organizationId ?? PERSON_CHOICE);
		} catch (err) {
			setCandidates([]);
			setChoice(PERSON_CHOICE);
			if (isAxiosError<ApiErrorResponse>(err) && err.response?.status === 400 && err.response.data?.code === "FUNDING_SOURCE_REQUIRED") {
				setFundingSourceMissing(true);
			} else if (isAxiosError<ApiErrorResponse>(err) && err.response?.status === 429) {
				setFundingSourceMissing(false);
				toast.warning(getErrorMessage(err, "Too many lookups. Try again later."));
			} else {
				setFundingSourceMissing(false);
				toast.error(getErrorMessage(err, "Failed to look up the email. Please try again."));
			}
		} finally {
			setLookedUpEmail(trimmedEmail);
			setIsLookingUp(false);
		}
	};

	const handleConfirm = () => {
		if (choice === PERSON_CHOICE) {
			createInvitationMutation.mutate({
				data: {
					email: trimmedEmail,
					roleCode: TALENT_ROLE,
					invitationType: InvitationType.Talent,
				},
			});
			return;
		}

		createPayeeOrganizationMutation.mutate({
			data: {
				email: trimmedEmail,
				payeeOrganizationId: choice,
			},
		});
	};

	const isConfirming = createPayeeOrganizationMutation.isPending || createInvitationMutation.isPending;
	const confirmLabel = choice === PERSON_CHOICE ? "Send Invitation" : "Add to Roster";

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Add a Payee"
				parent={{ url: "/roster", label: "Roster" }}
			/>
			<PageContentContainer className="space-y-6">
				<div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
					<Input
						label="Email Address"
						type="email"
						value={email}
						onChange={setEmail}
						isRequired
						autoFocus
						hint="The email of the person to pay or of an owner or administrator of the organization to pay."
					/>
					{trimmedEmail !== "" && !isEmailValid && (
						<p className="-mt-2 text-sm text-error-primary" role="alert">
							Enter a valid email address
						</p>
					)}

					{!hasLookedUp && (
						<Button
							color="primary"
							onClick={handleLookup}
							isLoading={isLookingUp}
							isDisabled={isLookingUp || !isEmailValid}
							className="w-full"
						>
							Continue
						</Button>
					)}

					{hasLookedUp && (
						<>
							<div>
								<div className="block text-sm font-medium text-gray-700 mb-2">
									Pay {trimmedEmail}
								</div>
								<RadioGroup aria-label="How this email is paid" value={choice} onChange={setChoice}>
									{candidates.map((candidate) => (
										<RadioButton
											key={candidate.organizationId}
											value={candidate.organizationId}
											label={`As ${candidate.name}`}
											hint="An organization paid to the deposit destination it chose. Its administrators are told it is on your roster."
										/>
									))}
									<RadioButton
										value={PERSON_CHOICE}
										label="As a talent member"
										hint="A person paid to their own payout method. They get an invitation to join your roster."
									/>
								</RadioGroup>
								{fundingSourceMissing && (
									<p className="mt-2 text-sm text-gray-500">
										Organizations can be added once yours has a funding source.
									</p>
								)}
							</div>

							<Button
								color="primary"
								onClick={handleConfirm}
								isLoading={isConfirming}
								isDisabled={isConfirming || !isEmailValid}
								className="w-full"
							>
								{confirmLabel}
							</Button>
						</>
					)}
				</div>
			</PageContentContainer>
		</AdminLayout>
	);
}
